/* eslint-disable import/no-cycle */
import React, { FC, useCallback, useState, useMemo, useEffect } from 'react';
import { useIntl } from 'react-intl';
import { generatePath, useParams } from 'react-router-dom';
import styled from 'styled-components';
import { Loader } from '@sdir/sds';
import { Field, Formik, Form } from 'formik';
import { useDispatch } from 'react-redux';
import * as Yup from 'yup';
import { ContactInfoRequest } from '@sdir/httpclient/lib/clients/core/commonaddressee';
import { useCurrentUser } from '@sdir/auth/lib/hooks';
import { useApi } from '@sdir/httpclient/lib/hooks/useApi';
import { FormSelect } from '@sdir/blueprint.aps/lib/components';
import { sortListByAttribute } from '@sdir/blueprint.aps/lib/helpers/formattingHelpers';
import { NewApplicationLayout } from '../..';
import FormInput from '../../../Molecules/FormInput';
import {
  codeElementsApi,
  contactInfoApi,
  countryApi,
  fregApi,
  updateApplicationApi,
} from '../../../../httpclient';
import { setContactInfo } from '../../../../store/actions';
import {
  useApplicationSteps,
  useCurrentApplication,
  useCurrentPerson,
  useCurrentRegisteredPerson,
  useCurrentUserInfo,
} from '../../../../services/hooks';
import TopSection from './TopSection';
import EditApplicationGuard from '../../../Molecules/EditApplicationGuard/EditApplicationGuard';
import { useNavigateWithLang } from '../../../../services/hooks/useNavigateWithLang';
import { FormRadioButtonGroup } from '../../../Molecules';
import FregInfo from './FregInfo';
import DialCodeList from '../../../../constants/lists';
import { splitPhoneNumber } from '../../../../helpers/formattingHelpers';

type ContactInfoFormValues = {
  dialCode?: string;
  phone?: string;
  gender?: string;
  email?: string;
};

const ContactInfo: FC = () => {
  const intl = useIntl();
  const requiredText = intl.formatMessage({ id: 'general.form.error' });
  const navigate = useNavigateWithLang();
  const pageParams = useParams<{ applicationUid: string }>();
  const { nextStep } = useApplicationSteps();
  const dispatch = useDispatch();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [apiCallError, setApiCallError] = useState(false);
  const { application } = useCurrentApplication();
  const currentUser = useCurrentUser();
  const { person: currentPerson, refresh: refreshPerson } = useCurrentPerson();
  const {
    userInfo: { isExternal },
  } = useCurrentUserInfo();
  const isSid = ['SID', 'SID_duplicate'].includes(application?.result?.certificate || '');

  const {
    person: { loading, result },
    refresh,
  } = useCurrentRegisteredPerson();

  const {
    result: countriesResults,
    errorObject: countriesError,
    loading: countriesLoading,
  } = useApi({
    apiFunction: () => countryApi.v2CountryGetallcountriesv2Get(),
    callApiOnInit: true,
  });

  const { result: fregResult, loading: fregLoading } = useApi({
    apiFunction: () => fregApi.v2FregGetownpersonGet(),
    callApiOnInit: true,
  });

  const genderOptions = useMemo(() => {
    return [
      { label: intl.formatMessage({ id: 'general.gender.M' }), value: 'M' },
      { label: intl.formatMessage({ id: 'general.gender.F' }), value: 'K' },
    ];
  }, [intl?.formatMessage]);

  const dialCodeOptions = useMemo(() => {
    const countries: any = countriesResults || [];
    const list = DialCodeList.map((d) => {
      const country = countries.find(
        (c) =>
          `${c?.alpha2 || ''}`.toLowerCase() === `${d?.secondValue || ''}`.toLowerCase() ||
          `${c?.code || ''}`.toLowerCase() === `${d?.secondValue || ''}`.toLowerCase()
      );
      return {
        name: country?.description || d.secondValue,
        value: d.value,
        secondValue: `+${d?.value || ''}`,
      };
    });
    const norwayIndex = list?.findIndex((country) => country.value === '47');
    const norway = list.splice(norwayIndex, 1);
    const sortedCountries = sortListByAttribute(list, 'name');
    return norway?.concat(sortedCountries);
  }, [countriesResults]);

  const initialValues: ContactInfoFormValues | null = useMemo(() => {
    if (!loading) {
      const fullPhone = currentPerson?.result?.phoneNumber;
      const { dialCode, phoneNumber } = splitPhoneNumber(fullPhone || '');
      return {
        dialCode: dialCode || '',
        phone: phoneNumber || '',
        email: currentPerson?.result?.email || '',
        gender: application?.result?.person?.gender || undefined,
      };
    }
    return null;
  }, [loading, application?.result?.person?.gender, currentPerson?.result]);

  const validationSchema = useMemo(() => {
    const numericRegex = /^\d+$/;
    const shape: any = {
      dialCode: Yup.mixed().required(requiredText),
      phone: Yup.mixed()
        .required(requiredText)
        .test('trimWhitespace', requiredText, (value) => value?.trim?.()?.length > 0)
        .test('isNumber', intl.formatMessage({ id: 'general.form.error.phone' }), (value) => {
          return numericRegex.test(value);
        })
        .test(
          'minLength',
          intl.formatMessage({ id: 'general.form.error.phone.length' }),
          (value) => value?.length >= 6
        )
        .test(
          'maxLength',
          intl.formatMessage({ id: 'general.form.error.phone.maxlength' }),
          (value) => value?.length < 15
        ),
      email: Yup.string()
        .email(intl.formatMessage({ id: 'general.form.error.email' }))
        .required(requiredText),
    };
    if (isExternal) {
      shape.gender = Yup.string().required(requiredText);
      delete shape.dialCode;
      delete shape.phone;
      delete shape.email;
    }
    return Yup.object().shape(shape);
  }, [intl, isExternal, requiredText]);

  const goToNextStep = useCallback(() => {
    if (nextStep) {
      navigate(
        generatePath(nextStep.route, {
          applicationUid: pageParams.applicationUid,
        })
      );
    }
  }, [nextStep, pageParams, navigate]);

  const handleSubmit = useCallback(
    async (values: ContactInfoFormValues) => {
      dispatch(setContactInfo(values));
      const legalEntityId = application?.result?.person?.legalEntityId;
      if (legalEntityId && !isExternal) {
        setIsSubmitting(true);
        setApiCallError(false);
        await codeElementsApi
          .v1CodeelementsCreatecontactinfoGet()
          .then((addressTypesResponse) => {
            if (addressTypesResponse.status === 200 && legalEntityId) {
              // eslint-disable-next-line max-len
              const contactInfoRequest: ContactInfoRequest[] = [];
              const addressTypePhone = addressTypesResponse.data.find((a) => a.code === 'TLF');
              const combinedPhone = `+${values?.dialCode || ''}${values?.phone || ''}`;
              contactInfoRequest.push({
                contactInfoTypeId: addressTypePhone?.id,
                contactData: combinedPhone,
                description: addressTypePhone?.description,
                preferredContact: true,
              });
              const addressTypeEmail = addressTypesResponse.data.find((a) => a.code === 'EPOST');
              contactInfoRequest.push({
                contactInfoTypeId: addressTypeEmail?.id,
                contactData: values.email,
                description: addressTypeEmail?.description,
                preferredContact: false,
              });
              contactInfoApi
                .v1ContactinfoCreateowncontactinfoCommandPost({
                  legalEntityId: +legalEntityId,
                  contactInfo: contactInfoRequest,
                })
                .then((response) => {
                  if (response.status === 200 || response.status === 201) {
                    goToNextStep();
                  }
                })
                .catch(() => {
                  setApiCallError(true);
                });
            }
          })
          .catch(() => {
            setApiCallError(true);
          });
      }
      if (isExternal) {
        await updateApplicationApi
          .v1UpdateapplicationSetgenderCommandPut({
            gender: values?.gender,
            uid: pageParams?.applicationUid,
          })
          .then(() => {
            goToNextStep();
          });
      }
      if (!legalEntityId && !isExternal) {
        goToNextStep();
      }
    },
    [dispatch, goToNextStep, application?.result, isExternal]
  );

  useEffect(() => {
    refresh?.();
    refreshPerson?.();
  }, []);

  return (
    <EditApplicationGuard>
      {loading || !initialValues ? (
        <Loader />
      ) : (
        <Formik
          initialValues={initialValues}
          onSubmit={handleSubmit}
          enableReinitialize
          validationSchema={validationSchema}
        >
          {({ submitForm }) => (
            <Form>
              <NewApplicationLayout
                exitLabel={intl.formatMessage({ id: 'general.form.back' })}
                mainContentMaxWidth="100%"
                canNavBack
                canNavNext={!isSubmitting}
                navBackLabel={intl.formatMessage({ id: 'general.form.cancel' })}
                navNextLabel={intl.formatMessage({ id: 'general.form.next' })}
                onNavNextClick={submitForm}
                onNavBackClick={() => navigate(-1)}
              >
                <Wrapper>
                  <TopSection
                    currentUser={currentUser}
                    person={result || null}
                    isExternal={isExternal}
                  />
                  {isSid ? <FregInfo person={fregResult} loading={fregLoading} /> : <></>}
                  {isExternal ? (
                    <FieldWrapper
                      name="gender"
                      radioContainer={CustomContainer}
                      component={FormRadioButtonGroup}
                      options={genderOptions}
                      label={intl.formatMessage({ id: 'contactinfo.gender' })}
                    />
                  ) : (
                    <>
                      <PhoneFields>
                        <Field
                          name="dialCode"
                          component={FormSelect}
                          searchable
                          options={dialCodeOptions}
                          defaultVal={intl.formatMessage({ id: 'contactinfo.dialCode' })}
                        />
                        <Field
                          name="phone"
                          component={FormInput}
                          label={intl.formatMessage({ id: 'contactinfo.phone' })}
                          type="text"
                        />
                      </PhoneFields>
                      <FieldWrapper
                        name="email"
                        component={FormInput}
                        label={intl.formatMessage({ id: 'contactinfo.email' })}
                        type="text"
                      />
                    </>
                  )}
                  <br />
                  {apiCallError && (
                    <ErrorMessageStyled>
                      {intl.formatMessage({ id: 'contactinfo.error' })}
                    </ErrorMessageStyled>
                  )}
                  {isSubmitting && <Loader />}
                </Wrapper>
              </NewApplicationLayout>
            </Form>
          )}
        </Formik>
      )}
    </EditApplicationGuard>
  );
};

export default ContactInfo;

const ErrorMessageStyled = styled.div`
  color: ${({ theme }) => theme.colors.error};
`;

const CustomContainer = styled.div`
  display: grid;
  grid-template-columns: auto auto;
  [data-id='base-item'] {
    padding: 1rem;
  }
`;

const PhoneFields = styled.div`
  display: flex;
  width: 25em;
  margin-bottom: 15px;

  & > div:first-child {
    width: calc(45% - 5px);
    margin-right: 5px;
  }
  & > div:last-child {
    width: 55%;
  }

  & [class^='Select__ElementList'] {
    width: max-content;
  }
`;

const FieldWrapper = styled(Field)`
  width: 25em;
  margin-bottom: 15px;
`;

const Wrapper = styled.div`
  width: 100%;
  background: white;
  padding: 3rem 5rem;
`;
